import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getPublicReschedule, doPublicReschedule, getAvailableSlots } from '../../services/api';
import { applyTenantBranding, clearTenantBranding } from '../../context/AuthContext';
import { formatDateTimeLong, formatTime } from '../../utils/formatDateTime';

export default function PublicReschedule() {
  const { slug, token } = useParams();
  const [appt,       setAppt]       = useState(null);
  const [tenant,     setTenant]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [done,       setDone]       = useState(false);
  const [step,       setStep]       = useState('info'); // info | slots
  const [slots,      setSlots]      = useState([]);
  const [slotsDate,  setSlotsDate]  = useState(() => new Date().toISOString().split('T')[0]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    getPublicReschedule(slug, token)
      .then(r => {
        setAppt(r.data.appointment);
        setTenant(r.data.tenant);
        if (r.data.tenant?.primary_color) {
          applyTenantBranding(r.data.tenant.primary_color);
        }
      })
      .catch(e => setError(e.response?.data?.error || 'Link inválido ou expirado'))
      .finally(() => setLoading(false));
    return () => clearTenantBranding();
  }, [slug, token]);

  const loadSlots = async date => {
    setSlotsDate(date);
    setLoadingSlots(true);
    setSelected(null);
    try {
      const r = await getAvailableSlots(slug, { date, service_id: appt?.service_id });
      setSlots(r.data.slots || []);
    } catch { toast.error('Erro ao buscar horários'); }
    finally { setLoadingSlots(false); }
  };

  useEffect(() => {
    if (step === 'slots' && appt) loadSlots(slotsDate);
  }, [step]); // eslint-disable-line

  const onConfirm = async () => {
    if (!selected) return toast.error('Selecione um horário');
    setSaving(true);
    try {
      await doPublicReschedule(slug, token, { new_datetime: selected });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao reagendar');
    } finally { setSaving(false); }
  };

  const fmtDate = formatDateTimeLong;
  const fmtTime = formatTime;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'var(--primary)', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {tenant?.logo_url
          ? <img src={tenant.logo_url} alt="logo" style={{ height: 40, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,.15)', padding: 3 }} />
          : <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700 }}>
              {tenant?.name?.[0] || '?'}
            </div>
        }
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{tenant?.name || 'Carregando…'}</div>
          <div style={{ fontSize: 12, opacity: .85 }}>Reagendamento</div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,.08)' }}>
          {loading && <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>}

          {!loading && error && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
              <h2 style={{ color: 'var(--gray-900)', marginBottom: 8 }}>Link inválido</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>{error}</p>
              <Link to={`/agendar/${slug}`} style={{ display: 'inline-block', marginTop: 20, color: 'var(--primary)', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>← Fazer novo agendamento</Link>
            </div>
          )}

          {!loading && done && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <h2 style={{ color: 'var(--gray-900)', marginBottom: 8 }}>Reagendamento confirmado</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 24 }}>
                Seu agendamento foi reagendado com sucesso para: <strong>{selected ? fmtDate(selected) : ''}</strong>
              </p>
              <Link to={`/agendar/${slug}`}
                style={{ display: 'inline-block', background: 'var(--primary)', color: '#fff', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                Voltar ao início
              </Link>
            </div>
          )}

          {!loading && !error && !done && appt && step === 'info' && (
            <>
              <h2 style={{ margin: '0 0 4px', color: 'var(--gray-900)', fontSize: 20 }}>Reagendar agendamento</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 24 }}>Agendamento atual:</p>

              <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
                {appt.service_name && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                    <span style={{ color: 'var(--gray-500)' }}>Serviço</span>
                    <span style={{ fontWeight: 600 }}>{appt.service_name}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                  <span style={{ color: 'var(--gray-500)' }}>Data e horário</span>
                  <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{fmtDate(appt.datetime)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span style={{ color: 'var(--gray-500)' }}>Duração</span>
                  <span style={{ fontWeight: 600 }}>{appt.duration_minutes} min</span>
                </div>
              </div>

              <button onClick={() => setStep('slots')} className="btn btn-primary" style={{ width: '100%' }}>
                Escolher novo horário →
              </button>
            </>
          )}

          {!loading && !error && !done && appt && step === 'slots' && (
            <>
              <button onClick={() => setStep('info')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
                ← Voltar
              </button>
              <h2 style={{ margin: '0 0 16px', color: 'var(--gray-900)', fontSize: 20 }}>Escolha a nova data</h2>

              <input type="date" className="input" value={slotsDate} min={new Date().toISOString().split('T')[0]}
                onChange={e => loadSlots(e.target.value)} style={{ marginBottom: 16 }} />

              {loadingSlots ? (
                <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" /></div>
              ) : slots.length === 0 ? (
                <p style={{ color: 'var(--gray-400)', textAlign: 'center', fontSize: 14 }}>Nenhum horário disponível para esta data.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
                  {slots.map(s => (
                    <button key={s} type="button"
                      onClick={() => setSelected(`${slotsDate}T${s}:00`)}
                      style={{
                        padding: '10px 0', borderRadius: 8, border: '2px solid',
                        borderColor: selected === `${slotsDate}T${s}:00` ? 'var(--primary)' : 'var(--gray-200)',
                        background: selected === `${slotsDate}T${s}:00` ? 'var(--primary)' : 'var(--surface)',
                        color: selected === `${slotsDate}T${s}:00` ? '#fff' : 'var(--gray-700)',
                        fontWeight: 600, fontSize: 14, cursor: 'pointer',
                      }}
                    >{s}</button>
                  ))}
                </div>
              )}

              {selected && (
                <div style={{ background: 'var(--green-50)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: 'var(--green-700)' }}>
                  Novo horário: <strong>{fmtDate(selected)}</strong>
                </div>
              )}

              <button onClick={onConfirm} disabled={!selected || saving}
                className={`btn btn-primary${saving ? ' btn-loading' : ''}`} style={{ width: '100%' }}>
                {saving ? '' : 'Confirmar reagendamento'}
              </button>
            </>
          )}
        </div>
      </div>

      {!tenant?.whitelabel && (
        <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-400)', fontSize: 12 }}>
          Powered by <strong style={{ color: 'var(--gray-500)' }}>AtendoApp</strong>
        </div>
      )}
    </div>
  );
}
